import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import MisActivoCard from '../components/MisActivoCard';
import EstadoActaCell from '../components/EstadoActaCell';
import { actasAPI, activosAPI, consumiblesAPI } from '../services/api';
import { Monitor, Calendar, PackageOpen, CheckCircle, List as ListIcon } from 'lucide-react';
import { FileText, Clock, LayoutGrid } from '../components/icons';


const MisActivos = () => {
  const { showToast } = useToast();
  const [activos, setActivos] = useState([]);
  const [actas, setActas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consumibles, setConsumibles] = useState([]);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('misActivosViewMode') || 'grid';
  });

  useEffect(() => {
    localStorage.setItem('misActivosViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    fetchMisActivos();
    fetchMisActas();
    fetchMisConsumibles();
  }, []);

  const fetchMisActivos = async () => {
    try {
      const response = await activosAPI.getMisActivos();
      setActivos(response.data);
    } catch (error) {
      console.error('Error:', error);
      showToast('Error', 'No se pudieron cargar tus activos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMisActas = async () => {
    try {
      const response = await actasAPI.getMisActas();
      setActas(response.data);
    } catch (error) {
      console.error('Error al cargar actas:', error);
    }
  };

  const fetchMisConsumibles = async () => {
    try {
      const response = await consumiblesAPI.getMisConsumibles();
      setConsumibles(response.data || []);
    } catch (error) {
      console.error('Error al cargar consumibles:', error);
    }
  };

  const getActaForActivo = (activoId) => {
    return Array.isArray(actas) ? actas.find(acta => acta.asignacion?.activo?.codigo === activoId) : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mis Activos
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Activos asignados a tu cuenta
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Activos</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{activos.length}</p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Monitor className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Actas Pendientes</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {Array.isArray(actas) ? actas.filter(acta => acta.estado === 'Pendiente').length : 0}
            </p>
          </div>
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <Clock className="w-6 h-6 text-amber-600 dark:text-amber-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Actas Aprobadas</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {Array.isArray(actas) ? actas.filter(acta => acta.estado === 'Aprobada').length : 0}
            </p>
          </div>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
            <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-500" />
          </div>
        </div>
      </div>

      {/* Activos List */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100 dark:border-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Equipos Asignados
        </h3>

        {/* Toggle Vista */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewMode === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            title="Vista de lista"
          >
            <ListIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            title="Vista de cuadrícula"
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
        </div>
      </div>

      {activos.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
          <Monitor className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No tienes activos asignados</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Los activos aparecerán aquí cuando te sean asignados.
          </p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? "grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" : "flex flex-col gap-3"}>
          {activos.map((activo) => (
            <MisActivoCard key={activo.codigo} activo={activo} acta={getActaForActivo(activo.codigo)} horizontal={viewMode === 'list'} />
          ))}
        </div>
      )}

      {/* Consumibles Asignados */}
      {consumibles.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 pb-2 border-b border-gray-100 dark:border-gray-800">
            Consumibles Recibidos
          </h3>
          <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "flex flex-col gap-3"}>
            {consumibles.map((mov) => {
              const acta = mov.actas && mov.actas.length > 0 ? mov.actas[0] : null;
              return (
                <div key={mov.id} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all overflow-hidden flex ${viewMode === 'list' ? 'flex-col sm:flex-row items-stretch' : 'flex-col h-full'}`}>
                  <div className={`p-5 flex-grow ${viewMode === 'list' ? 'flex flex-col sm:flex-row sm:items-center gap-4 border-b sm:border-b-0 sm:border-r border-gray-100 dark:border-gray-700 w-full' : ''}`}>
                    <div className={`flex justify-between items-start ${viewMode === 'grid' ? 'mb-4' : 'flex-grow min-w-[200px]'}`}>
                      <div className="flex items-center space-x-3">
                        <div className="p-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300 flex-shrink-0">
                          <PackageOpen className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
                            {mov.consumible?.nombre}
                          </h4>
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {mov.consumible?.categoria}
                          </span>
                        </div>
                      </div>
                      {viewMode === 'grid' && (
                        <div className="flex items-center justify-center min-w-8 h-8 px-2 bg-gray-100 dark:bg-gray-700 rounded-full font-bold text-gray-700 dark:text-gray-300 text-sm">
                          x{mov.cantidad}
                        </div>
                      )}
                    </div>

                    <div className={`${viewMode === 'list' ? 'flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-6 min-w-[200px]' : 'space-y-2 mt-4'}`}>
                      <div className="flex justify-between items-center text-sm gap-2 whitespace-nowrap">
                        <span className="text-gray-500 dark:text-gray-400 flex items-center">
                          <Calendar className={`w-4 h-4 mr-1.5 ${viewMode === 'list' ? 'hidden sm:block' : ''}`} />
                          Fecha Entrega:
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {mov.fecha ? new Date(mov.fecha).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>

                    {viewMode === 'list' && (
                      <div className="hidden sm:flex items-center justify-center min-w-8 h-8 px-3 ml-auto mr-4 bg-gray-100 dark:bg-gray-700 rounded-full font-bold text-gray-700 dark:text-gray-300 text-sm">
                        Cant: {mov.cantidad}
                      </div>
                    )}
                  </div>

                  <div className={`px-5 py-3 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 ${viewMode === 'list' ? 'sm:w-48 sm:flex-col sm:justify-center gap-1 flex-shrink-0' : 'border-t border-gray-100 dark:border-gray-700'}`}>
                    <span className={`text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center ${viewMode === 'list' ? 'text-center' : ''}`}>
                      <FileText className={`w-4 h-4 mr-1.5 ${viewMode === 'list' ? 'hidden lg:block' : ''}`} />
                      Estado del Acta
                    </span>
                    <EstadoActaCell acta={acta} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div >
  );
};

export default MisActivos; 